// ─── src/components/elevateFeedback/analysis.ts ──────────────────────────────
//
// Arabic labels (as participants saw them on the Elevate Damascus 2026
// feedback form), aggregate stats, auto-generated highlights and the CSV
// export for the staff "Elevate Feedback" view.

import type {
  ElevateDayKey,
  ElevateFeedback,
  ElevateOrgAspect,
  ElevatePillarKey,
} from "../../api/elevate";
import type { RatingStats } from "../../api/gewFeedback";

export const DAY_LABEL: Record<ElevateDayKey, string> = {
  day1: "اليوم الأول",
  day2: "اليوم الثاني",
  day3: "اليوم الثالث",
};
export const DAY_KEYS: ElevateDayKey[] = ["day1", "day2", "day3"];

/** English title · speaker, as on the delivered agenda and the form. */
export const SESSION_LABEL: Record<string, string> = {
  d1_kickoff: "The Kickoff: Meet the Ecosystem · Startup Syria",
  d1_funding_playbook: "The Startup Funding Playbook · Startup Syria",
  d1_vc_engine: "Inside the VC Engine · Startup Syria",
  d1_first_check: "The First Check: Inside an Angel Investor's Mind · Abdullah Shamma",
  d1_beyond_pitch: "Beyond the Pitch: What Founders Learned About Raising Capital · Bee Order, Doushesh, Key Finder",
  d2_deal_terms: "Deal Terms Decoded · Startup Syria",
  d2_due_diligence: "Startup Due Diligence 101 · Startup Syria",
  d2_million_deal: "Inside a $1M Startup Deal · Startup Syria",
  d2_top_candidate: "Selecting the Top Candidate · Anthony Chedid",
  d2_funding_startup: "Funding Your Startup · Saleem Najjar",
  d2_seed_to_scale: "From Seed to Scale: Real Startup Growth Stories · Madfotacom, Avalon, Caroline Ayoub",
  d3_continuous_support: "Continuous Support · Mohamad Kaswani",
  d3_beyond_borders: "Beyond Borders: Lessons from Global Startup Ecosystems · Ramy Bujawdeh",
  d3_venture_investor: "Thinking Like a Venture Investor · Jad Mneymneh",
  d3_deal_room: "The Deal Room: Term Sheets & Investment Simulation · Startup Syria",
};
export const SESSION_KEYS = Object.keys(SESSION_LABEL);
export const sessionDay = (key: string): ElevateDayKey | null =>
  key.startsWith("d1_") ? "day1" : key.startsWith("d2_") ? "day2" : key.startsWith("d3_") ? "day3" : null;

export const PILLAR_LABEL: Record<ElevatePillarKey, string> = {
  investing: "الاستثمار في الشركات الناشئة وآلياته",
  ecosystems: "فهم منظومات الشركات الناشئة وما يحفّز نموها",
  network: "بناء الثقة وتوسيع شبكة العلاقات",
  cases: "التعلّم من تجارب ودراسات حالة واقعية",
  mentoring: "إرشاد الشركات الناشئة",
};
export const PILLAR_KEYS: ElevatePillarKey[] = ["investing", "ecosystems", "network", "cases", "mentoring"];

export const ORG_LABEL: Record<ElevateOrgAspect, string> = {
  logistics: "التنظيم والخدمات اللوجستية",
  agenda: "الأجندة",
  commsBefore: "التواصل قبل البرنامج",
  commsDuring: "التواصل خلال البرنامج",
};
export const ORG_KEYS: ElevateOrgAspect[] = ["logistics", "agenda", "commsBefore", "commsDuring"];

export const DURATION_LABEL: Record<string, string> = {
  short: "قصيرة",
  right: "مناسبة",
  long: "طويلة",
};

// ─── Stats ───────────────────────────────────────────────────────────────────

export const pct = (n: number, total: number) => (total ? Math.round((n / total) * 100) : 0);
const round1 = (n: number) => Math.round(n * 10) / 10;

export const avg = (vals: (number | null | undefined)[]): number | null => {
  const v = vals.filter((x): x is number => typeof x === "number");
  return v.length ? round1(v.reduce((a, b) => a + b, 0) / v.length) : null;
};

export const ratingStats = (vals: (number | null | undefined)[]): RatingStats => {
  const v = vals.filter((x): x is number => typeof x === "number");
  const distribution = [0, 0, 0, 0, 0];
  v.forEach((x) => {
    if (x >= 1 && x <= 5) distribution[Math.round(x) - 1] += 1;
  });
  return { average: avg(v), answered: v.length, distribution };
};

/** Counts for every key in `keys` (zeros kept), plus any unexpected keys seen. */
export const countKeys = (values: (string | null | undefined)[], keys: string[]): Record<string, number> => {
  const out: Record<string, number> = Object.fromEntries(keys.map((k) => [k, 0]));
  values.forEach((v) => {
    if (v) out[v] = (out[v] ?? 0) + 1;
  });
  return out;
};

export interface NpsStats {
  score: number | null;
  average: number | null;
  promoters: number;
  passives: number;
  detractors: number;
  /** Counts for 0..10. */
  distribution: number[];
}

export const npsStats = (rows: ElevateFeedback[]): NpsStats => {
  const distribution = Array.from({ length: 11 }, () => 0);
  let promoters = 0;
  let passives = 0;
  let detractors = 0;
  rows.forEach((r) => {
    if (typeof r.nps !== "number") return;
    distribution[Math.max(0, Math.min(10, Math.round(r.nps)))] += 1;
    if (r.nps >= 9) promoters += 1;
    else if (r.nps >= 7) passives += 1;
    else detractors += 1;
  });
  const total = promoters + passives + detractors;
  return {
    score: total ? Math.round(((promoters - detractors) / total) * 100) : null,
    average: avg(rows.map((r) => r.nps)),
    promoters,
    passives,
    detractors,
    distribution,
  };
};

export interface ElevateSummary {
  responses: number;
  overall: RatingStats;
  speakers: RatingStats;
  networking: RatingStats;
  interaction: RatingStats;
  mentorConfidence: RatingStats;
  /** Average of every organisation-aspect rating. */
  organizationAvg: number | null;
  nps: NpsStats;
  days: { key: ElevateDayKey; average: number | null; attended: number }[];
  sessions: { key: string; count: number }[];
  pillars: { key: ElevatePillarKey; average: number | null }[];
  organization: { key: ElevateOrgAspect; average: number | null }[];
  duration: Record<string, number>;
  quotes: number;
}

export const summarise = (rows: ElevateFeedback[]): ElevateSummary => {
  return {
    responses: rows.length,
    overall: ratingStats(rows.map((r) => r.ratingOverall)),
    speakers: ratingStats(rows.map((r) => r.ratingSpeakers)),
    networking: ratingStats(rows.map((r) => r.ratingNetworking)),
    interaction: ratingStats(rows.map((r) => r.ratingInteraction)),
    mentorConfidence: ratingStats(rows.map((r) => r.mentorConfidence)),
    organizationAvg: avg(rows.flatMap((r) => ORG_KEYS.map((k) => r.organizationRatings?.[k]))),
    nps: npsStats(rows),
    days: DAY_KEYS.map((key) => {
      const v = rows.map((r) => r.dayRatings?.[key]).filter((x): x is number => typeof x === "number");
      return { key, average: avg(v), attended: v.length };
    }),
    sessions: Object.entries(countKeys(rows.flatMap((r) => r.topSessions ?? []), SESSION_KEYS))
      .map(([key, count]) => ({ key, count }))
      .sort((a, b) => b.count - a.count || SESSION_KEYS.indexOf(a.key) - SESSION_KEYS.indexOf(b.key)),
    pillars: PILLAR_KEYS.map((key) => ({ key, average: avg(rows.map((r) => r.pillarRatings?.[key])) })),
    organization: ORG_KEYS.map((key) => ({ key, average: avg(rows.map((r) => r.organizationRatings?.[key])) })),
    duration: countKeys(rows.map((r) => r.durationFit), Object.keys(DURATION_LABEL)),
    quotes: rows.filter((r) => r.quoteConsent && r.quote).length,
  };
};

// ─── Highlights ──────────────────────────────────────────────────────────────

export interface Highlight {
  /** English lead-in. */
  text: string;
  /** Optional Arabic label shown after the lead-in, right-to-left. */
  ar?: string;
  tone: "good" | "warn" | "neutral";
}

/** Short, plain-language findings computed from the answers. */
export const highlights = (s: ElevateSummary): Highlight[] => {
  if (!s.responses) return [];
  const out: Highlight[] = [];
  const n = s.responses;

  const top = s.sessions[0];
  if (top && top.count > 0) {
    const tied = s.sessions.filter((x) => x.count === top.count).length;
    out.push({
      text: `Most useful session${tied > 1 ? ` (tied with ${tied - 1} other${tied > 2 ? "s" : ""})` : ""}: picked by ${top.count} of ${n} (${pct(top.count, n)}%)`,
      ar: SESSION_LABEL[top.key] ?? top.key,
      tone: "good",
    });
  }
  const unpicked = s.sessions.filter((x) => x.count === 0).length;
  if (unpicked > 0) {
    out.push({ text: `${unpicked} of ${s.sessions.length} sessions were never picked as most useful`, tone: "neutral" });
  }

  const rated = s.days.filter((d) => d.average != null);
  if (rated.length > 1) {
    const low = [...rated].sort((a, b) => (a.average ?? 0) - (b.average ?? 0))[0];
    const high = [...rated].sort((a, b) => (b.average ?? 0) - (a.average ?? 0))[0];
    if (low.average !== high.average) {
      out.push({ text: `Lowest-rated day: ${low.average} / 5 (best ${high.average})`, ar: DAY_LABEL[low.key], tone: "warn" });
    } else {
      out.push({ text: `All days rated the same: ${low.average} / 5`, tone: "neutral" });
    }
  }
  const leastAttended = [...s.days].sort((a, b) => a.attended - b.attended)[0];
  if (leastAttended && leastAttended.attended < n) {
    out.push({
      text: `${n - leastAttended.attended} respondent${n - leastAttended.attended !== 1 ? "s" : ""} skipped the least-attended day`,
      ar: DAY_LABEL[leastAttended.key],
      tone: "neutral",
    });
  }

  const pillars = s.pillars.filter((p) => p.average != null).sort((a, b) => (b.average ?? 0) - (a.average ?? 0));
  if (pillars.length > 1 && pillars[0].average !== pillars[pillars.length - 1].average) {
    out.push({ text: `Strongest pillar: ${pillars[0].average} / 5`, ar: PILLAR_LABEL[pillars[0].key], tone: "good" });
    const w = pillars[pillars.length - 1];
    out.push({ text: `Weakest pillar: ${w.average} / 5`, ar: PILLAR_LABEL[w.key], tone: "warn" });
  }

  const org = s.organization.filter((o) => o.average != null).sort((a, b) => (a.average ?? 0) - (b.average ?? 0));
  if (org.length > 1 && org[0].average !== org[org.length - 1].average) {
    out.push({ text: `Weakest part of the organisation: ${org[0].average} / 5`, ar: ORG_LABEL[org[0].key], tone: "warn" });
  }
  out.push({
    text: `${pct(s.duration.right ?? 0, n)}% said the length was right` +
      ` (${s.duration.short ?? 0} too short, ${s.duration.long ?? 0} too long)`,
    tone: pct(s.duration.right ?? 0, n) >= 60 ? "good" : "warn",
  });

  const mc = s.mentorConfidence;
  if (mc.answered) {
    const confident = mc.distribution[3] + mc.distribution[4];
    out.push({
      text: `${pct(confident, mc.answered)}% feel confident (4–5) mentoring a startup founder after the programme`,
      tone: pct(confident, mc.answered) >= 60 ? "good" : "warn",
    });
  }
  if (s.nps.score != null) {
    out.push({
      text: `NPS ${s.nps.score > 0 ? "+" : ""}${s.nps.score}: ${s.nps.promoters} promoter${s.nps.promoters !== 1 ? "s" : ""}, ${s.nps.detractors} detractor${s.nps.detractors !== 1 ? "s" : ""}`,
      tone: s.nps.score >= 30 ? "good" : s.nps.score >= 0 ? "neutral" : "warn",
    });
  }
  return out;
};

// ─── Text helpers ────────────────────────────────────────────────────────────

export const OPEN_FIELDS = [
  ["weakSession", "Weakest session", "الجلسة الأقل فائدة"],
  ["supportNeeds", "Support needed", "الدعم المطلوب"],
  ["futureTopics", "Future topics", "مواضيع مقترحة للمستقبل"],
  ["otherNotes", "Other notes", "ملاحظات أخرى"],
] as const;
export type OpenFieldKey = (typeof OPEN_FIELDS)[number][0];

/** Every free-text answer in one string, for the search box. */
export const searchableText = (r: ElevateFeedback) =>
  [r.keyTakeaway, r.weakSession, r.supportNeeds, r.futureTopics, r.otherNotes, r.quote, r.fullName, r.roleOrg]
    .filter(Boolean)
    .join(" \n ")
    .toLowerCase();

// ─── CSV ─────────────────────────────────────────────────────────────────────

/** CSV (UTF-8 with BOM so Excel shows Arabic) of every answer, one row per response. */
export function exportElevateFeedbackCsv(rows: ElevateFeedback[], filename: string) {
  const esc = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const day = (v: number | null | undefined) => (v == null ? "لم يحضر" : v);
  const header = [
    "Submitted at",
    "Overall (1–5)", "NPS (0–10)", "Key takeaway",
    "Day 1 (1–5)", "Day 2 (1–5)", "Day 3 (1–5)",
    "Most useful sessions", "Weakest session",
    "Speakers (1–5)", "Interaction & workshops (1–5)",
    ...PILLAR_KEYS.map((k) => `Pillar: ${PILLAR_LABEL[k]} (1–5)`),
    "Network built (1–5)",
    ...ORG_KEYS.map((k) => `Organisation: ${ORG_LABEL[k]} (1–5)`),
    "Duration", "Mentoring confidence (1–5)",
    "Support needed", "Future topics", "Other notes",
    "Quote consent", "Quote", "Name", "Role / organisation",
  ].map(esc).join(",");
  const body = rows.map((r) =>
    [
      r.createdAt,
      r.ratingOverall, r.nps, r.keyTakeaway,
      day(r.dayRatings?.day1), day(r.dayRatings?.day2), day(r.dayRatings?.day3),
      (r.topSessions ?? []).map((k) => SESSION_LABEL[k] ?? k).join("، "), r.weakSession,
      r.ratingSpeakers, r.ratingInteraction,
      ...PILLAR_KEYS.map((k) => r.pillarRatings?.[k]),
      r.ratingNetworking,
      ...ORG_KEYS.map((k) => r.organizationRatings?.[k]),
      DURATION_LABEL[r.durationFit] ?? r.durationFit, r.mentorConfidence,
      r.supportNeeds, r.futureTopics, r.otherNotes,
      r.quoteConsent ? "Yes" : "No", r.quote, r.fullName, r.roleOrg,
    ].map(esc).join(","),
  );
  const blob = new Blob(["﻿" + [header, ...body].join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
